"use client";

/**
 * The deal detail panel's stage-history read.
 *
 * One GET, and the one rule this hook exists to keep: a FAILED read is not an
 * empty history. "No stage history" and "we could not load the stage history"
 * look identical if the failure is swallowed, and the first is a legitimate
 * state (a deal whose backfill row was pruned) — so the card must be able to
 * tell them apart and offer a retry.
 */

import { useCallback, useEffect, useState } from "react";

import {
  isDealStageOccupancy,
  refreshOccupancyDurations,
  type DealStageOccupancy,
} from "@west-tennessee-consulting/deal-stages";

const READ_FAILED = "Could not load this deal's stage history.";

export interface DealStageHistoryState {
  loading: boolean;
  /** Newest entry first; `[]` only when the read SUCCEEDED and found nothing. */
  entries: DealStageOccupancy[];
  /** The stage the deal is in now, with how long it has been there. */
  current: DealStageOccupancy | null;
  error: string | null;
  retry: () => void;
}

/**
 * Validate the payload, then re-measure against NOW: the server computed the
 * durations at request time, and a panel left open overnight must not still be
 * claiming "2 hours".
 */
function readEntries(body: unknown): DealStageOccupancy[] {
  if (typeof body !== "object" || body === null) return [];
  if (!("entries" in body) || !Array.isArray(body.entries)) return [];
  const entries: DealStageOccupancy[] = body.entries.filter(isDealStageOccupancy);
  return refreshOccupancyDurations(entries, new Date());
}

export function useDealStageHistory(dealId: string): DealStageHistoryState {
  const [entries, setEntries] = useState<DealStageOccupancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/deals/${encodeURIComponent(dealId)}/stage-history`, {
        credentials: "same-origin",
      });
      if (!res.ok) {
        setError(READ_FAILED);
        return;
      }
      setEntries(readEntries(await res.json()));
      setError(null);
    } catch {
      setError(READ_FAILED);
    } finally {
      // ASYNC_EFFECT_COMPLETION: the loading flag clears in `finally`, so an
      // early return above can never leave the card spinning forever.
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    void load();
  }, [load]);

  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    void load();
  }, [load]);

  return { loading, entries, current: entries[0] ?? null, error, retry };
}
